import { Vue, Component, Ref, Emit } from 'vue-property-decorator'
import BillingModalDeliveryNotesCrudCardComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/components/card/billing-modal-delivery-notes-crud-card.vue'
import BillingModalDeliveryNotesCrudForm from '@/modules/billing-module/components/modal/delivery-notes/crud/components/form/billing-modal-delivery-notes-crud-form.vue'
import BillingModalDeliveryNotesCrudGridComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/components/grid/billing-modal-delivery-notes-crud-grid.vue'
import BillingModalDeliveryNotesCrudActionsComponent from '@/modules/billing-module/components/modal/delivery-notes/crud/components/actions/billing-modal-delivery-notes-crud-actions.vue'
import BillingModalCustomerSelectionComponent from '@/modules/billing-module/components/modal/customer/selection/billing-modal-customer-selection.vue'
import BillingModalCustomerSelection from '@/modules/billing-module/components/modal/customer/selection/billing-modal-customer-selection'
import { BModal } from 'bootstrap-vue'
import {
    BillingModalDeliveryNotesCrudParams,
    BillingModalDeliveryNotesCrudState,
    initialBillingModalDeliveryNotesCrudState
} from '@/modules/billing-module/components/modal/delivery-notes/crud/billing-modal-delivery-notes-crud.model'
import { BillableDeliveryNoteStatus } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import {
    AppliedBillableConceptExtraFieldsPatchInBatchRequest,
    AppliedBillableConceptPatchInBatchRequest
} from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.dto'
import { AppliedBillableConceptChangedEvent } from '@/modules/billing-module/components/modal/delivery-notes/crud/components/grid/billing-modal-delivery-notes-crud-grid.model'
import { CustomerResponse } from '@/api/services/billing/common-customer/billing-common-customer.dto'
import { cloneDeep } from 'lodash'
import { billingCommonDeliveryNoteService } from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { billingAppliedBillableConceptService } from '@/api/services/billing/applied-billable-concept/billing-applied-billable-concept.service'
import { store } from '@/store/store'
import { PromiseUtils } from '@/shared/util/promise-utils'
import axios from 'axios'

@Component({
    components: {
        BillingModalDeliveryNotesCrudCardComponent,
        BillingModalDeliveryNotesCrudForm,
        BillingModalDeliveryNotesCrudGridComponent,
        BillingModalDeliveryNotesCrudActionsComponent,
        BillingModalCustomerSelectionComponent
    }
})
export default class BillingModalDeliveryNotesCrud extends Vue {
    @Ref() readonly deliveryNotesModal!: BModal
    @Ref() readonly customerSelectionModal!: BillingModalCustomerSelection

    @Emit()
    refresh(): void {
        // emits an event
    }

    @Emit()
    deleteDeliveryNoteWithoutConcepts(): void {
        // emits an event
    }

    state: BillingModalDeliveryNotesCrudState
    constructor() {
        super()
        this.state = initialBillingModalDeliveryNotesCrudState()
    }

    /// COMPUTED ///

    get modalTitle(): string {
        return this.state.deliveryNote
            ? this.$tc('accounting.action.updateDeliveryNote')
            : this.$tc('accounting.action.generateDeliveryNote')
    }
    get allConceptsHasClient(): boolean {
        return this.state.appliedBillableConcepts.every(appliedBillableConcept => appliedBillableConcept.customer)
    }
    get conceptsIds(): number[] {
        return this.state.appliedBillableConcepts.map(appliedBillableConcept => appliedBillableConcept.id)
    }
    get deliveryNoteStatusCode(): BillableDeliveryNoteStatus | undefined {
        return this.state.deliveryNote?.status.code
    }

    get visibleDeliveryNoteModalForm(): boolean {
        return !!this.state.deliveryNote
    }

    /// MODAL ///

    show(params: BillingModalDeliveryNotesCrudParams): void {
        this.state = initialBillingModalDeliveryNotesCrudState()

        const { deliveryNote, appliedBillableConcepts } = params
        if (appliedBillableConcepts) {
            this.state.appliedBillableConcepts = cloneDeep(appliedBillableConcepts)
        }
        if (deliveryNote) {
            this.state.deliveryNote = cloneDeep(deliveryNote)
        }

        if (this.allConceptsHasClient) {
            this.deliveryNotesModal.show()
        } else {
            if (this.state.appliedBillableConcepts.length) {
                const { patient } = this.state.appliedBillableConcepts[0].caseStudy
                this.customerSelectionModal.show({ patient })
            }
        }
    }
    hide(): void {
        this.deliveryNotesModal.hide()
    }

    /// EVENT HANDLERS ///

    private async _reloadDeliveryNote(deliveryNoteId: number): Promise<boolean> {
        this.state.source = axios.CancelToken.source()
        const response =
            await PromiseUtils.toCallSecure(
                billingCommonDeliveryNoteService.get(store.state.labId, deliveryNoteId, this.state.source.token))
        if (response !== null) {
            this.state.deliveryNote = response
        }
        return response !== null
    }
    async onRefresh(deliveryNoteId: number): Promise<void> {
        this.state.source?.cancel()
        if (await this._reloadDeliveryNote(deliveryNoteId)) {
            this.refresh()
        }
    }

    private async _assignCustomerToAppliedBillableConcepts(customer: CustomerResponse): Promise<boolean> {
        const request =
            this.state.appliedBillableConcepts.map((abc): AppliedBillableConceptPatchInBatchRequest => ({
                id: abc.id,
                customerId: customer.id
            }))
        const response =
            await PromiseUtils.toCallSecure(
                billingAppliedBillableConceptService.patch(
                    store.state.labId,
                    request))
        return response !== null
    }
    async onCustomerSelected(customer: CustomerResponse): Promise<void> {
        if (await this._assignCustomerToAppliedBillableConcepts(customer)) {
            this.state.customer = customer
            if (this.state.appliedBillableConcepts.length) {
                for (const appliedBillableConcept of this.state.appliedBillableConcepts) {
                    appliedBillableConcept.customer = customer
                }
            }

            this.refresh()
            this.deliveryNotesModal.show()
        }
    }

    onConceptsCountChanged(count: number): void {
        this.state.deliveryNoteConceptsCount = count
    }

    private _refreshDeliveryNoteConceptsTotalValue(subtract: number, add: number): void {
        if (!!this.state.deliveryNote && this.state.deliveryNote.conceptsTotalValue !== null) {
            let ret = this.state.deliveryNote.conceptsTotalValue * 100
            ret = ret - (subtract * 100) + (add * 100)
            this.state.deliveryNote.conceptsTotalValue = ret / 100
        }
    }
    onAppliedBillableConceptChanged({ field, data, oldValue, newValue }: AppliedBillableConceptChangedEvent): void {
        const { id, value, autoInvoiceSystemInError, autoInvoiceSystemOperationCode, autoInvoiceSystemErrorDescription } = data
        const request: AppliedBillableConceptExtraFieldsPatchInBatchRequest = {
            id,
            value,
            autoInvoiceSystemInError,
            autoInvoiceSystemOperationCode: autoInvoiceSystemInError ? autoInvoiceSystemOperationCode : null,
            autoInvoiceSystemErrorDescription: autoInvoiceSystemInError ? autoInvoiceSystemErrorDescription : null
        }

        const index = this.state.appliedBillableConceptsToUpdate.findIndex(abc => abc.id === id)
        if (index !== -1) {
            this.state.appliedBillableConceptsToUpdate[index] = request
        } else {
            this.state.appliedBillableConceptsToUpdate.push(request)
        }

        if (field === 'value') {
            this._refreshDeliveryNoteConceptsTotalValue(oldValue, newValue)
        }
    }

    async onAppliedBillableConceptsDeleted(): Promise<void> {
        if (this.state.deliveryNote) {
            await this.onRefresh(this.state.deliveryNote.id)
            if (this.state.deliveryNote.status.code === BillableDeliveryNoteStatus.ISSUED && !this.state.deliveryNote.numberOfconcepts) {
                this.deleteDeliveryNoteWithoutConcepts()
            }
        }

        if (!this.state.deliveryNote && !this.state.appliedBillableConcepts.length) {
            this.hide()
        }
    }
}
